import React, { useState, useEffect, useCallback } from "react";
import { FaCalendarCheck } from "react-icons/fa";
import API_BASE from "./apiConfig";
import { Colors } from "./branding";
import { useTheme, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import { useDateTimeFormat } from "./utils/formatters";
import { useAppConfig } from "./AppContext";

const C = { get yellow() { return getBrandColors().yellow; }, get blue() { return getBrandColors().blue; } };
const DAYS_SHORT = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const MONTHS_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const card = (T) => ({
  background: T ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.90)",
  border: T ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(3,78,123,0.12)",
  borderRadius: 14, padding: "18px 20px", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
});
const btn = (primary, sm, disabled) => ({
  padding: sm ? "6px 14px" : "9px 20px", borderRadius: 8, border: "none",
  cursor: disabled ? "not-allowed" : "pointer", fontSize: sm ? 12 : 13, fontWeight: 700,
  background: disabled ? (primary ? "rgba(247,206,11,0.4)" : "rgba(3,78,123,0.35)") : (primary ? C.yellow : C.blue),
  color: primary ? "#1a1200" : "#fff", transition: "opacity 0.14s", opacity: disabled ? 0.6 : 1,
});
const inputSt = (T) => ({
  width: "100%", boxSizing: "border-box", padding: "8px 11px", borderRadius: 8, fontSize: 13,
  background: T ? "rgba(255,255,255,0.07)" : "#f4f7fb", outline: "none",
  border: T ? "1px solid rgba(255,255,255,0.13)" : "1px solid rgba(3,78,123,0.18)",
  color: T ? "rgba(255,255,255,0.88)" : "#1a2a3a",
});

// Build calendar weeks for a given month/year
function buildCalendar(year, month) {
  const first = new Date(year, month - 1, 1);
  const last  = new Date(year, month, 0);
  const startDow = (first.getDay() + 6) % 7; // 0=Mon
  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(d);
  while (days.length % 7 !== 0) days.push(null);
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

export default function OTOBooking({ currentUser }) {
  const { isDark: T } = useTheme();
  const textPrimary   = T ? "rgba(255,255,255,0.88)" : "#1a2a3a";
  const textSecondary = T ? "rgba(255,255,255,0.50)" : "rgba(3,78,123,0.55)";
  const { fmtDate, fmtTime } = useDateTimeFormat();
  const { config: appConfig } = useAppConfig();
  const currency = (appConfig?.currency || "USD").toUpperCase();

  const now = new Date();
  const [coaches,    setCoaches]    = useState([]);
  const [students,   setStudents]   = useState([]);
  const [selCoach,   setSelCoach]   = useState(null);
  const [calYear,    setCalYear]    = useState(now.getFullYear());
  const [calMonth,   setCalMonth]   = useState(now.getMonth() + 1);
  const [selDate,    setSelDate]    = useState(null);
  // monthSlots: { "YYYY-MM-DD": [{start_time,end_time,...}] } — prefetched for whole month
  const [monthSlots,   setMonthSlots]   = useState({});  // { "YYYY-MM-DD": slot[] }
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selSlot,      setSelSlot]      = useState(null);
  const [modal,      setModal]      = useState(false);
  const [selStudent, setSelStudent] = useState("");
  const [stuSearch,  setStuSearch]  = useState("");
  const [stuOpen,    setStuOpen]    = useState(false);
  const [notes,      setNotes]      = useState("");
  const [booking,    setBooking]    = useState(false);
  const [result,     setResult]     = useState(null);
  const [error,      setError]      = useState("");
  const [parentCard, setParentCard] = useState(null);
  const [cardLoading,setCardLoading]= useState(false);

  // Coach rates panel
  const [editRateId,  setEditRateId]  = useState(null);
  const [rateVal,     setRateVal]     = useState("");
  const [payRateVal,  setPayRateVal]  = useState("");
  const [rateSaving,  setRateSaving]  = useState(false);
  const [rateMsg,     setRateMsg]     = useState(null);

  const isAdmin = currentUser?.user_role === "Admin" || currentUser?.is_superadmin;

  useEffect(() => {
    fetch(`${API_BASE}/oto/coaches`).then(r => r.json()).then(d => setCoaches(d.coaches || []));
  }, []);

  // Prefetch all slots for the selected coach + month in ONE call
  const fetchMonthSlots = useCallback(async (coachId, year, month) => {
    setSlotsLoading(true);
    setMonthSlots({});
    setSelDate(null);
    setSelSlot(null);
    try {
      const res = await fetch(`${API_BASE}/oto/month-slots?coach_id=${coachId}&year=${year}&month=${month}`);
      const d   = await res.json();
      setMonthSlots(d.month_slots || {});
    } catch { setMonthSlots({}); }
    setSlotsLoading(false);
  }, []);

  // Refetch current month (called after a booking is made to remove the booked slot)
  const refreshMonthSlots = useCallback((coachId, year, month) => {
    fetchMonthSlots(coachId, year, month);
  }, [fetchMonthSlots]);

  // When coach or month/year changes, prefetch
  useEffect(() => {
    if (selCoach) fetchMonthSlots(selCoach.user_id, calYear, calMonth);
    else { setMonthSlots({}); setSelDate(null); setSelSlot(null); }
  }, [selCoach, calYear, calMonth, fetchMonthSlots]);

  const searchStudents = useCallback(async (q) => {
    if (!q || q.trim().length < 1) { setStudents([]); return; }
    try {
      const res  = await fetch(`${API_BASE}/students/search?name=${encodeURIComponent(q.trim())}`);
      const data = await res.json();
      setStudents(data.students || []);
    } catch { setStudents([]); }
  }, []);

  // Instant — reads from prefetched cache
  const pickDate = (day) => {
    if (!day || !selCoach || slotsLoading) return;
    const dateStr = `${calYear}-${String(calMonth).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    if (dateStr < todayStr) return;
    setSelDate(dateStr);
    setSelSlot(null);
  };

  const openModal = (slot) => {
    setSelSlot(slot); setModal(true); setResult(null); setError("");
    setSelStudent(""); setStuSearch(""); setStuOpen(false); setNotes("");
    setParentCard(null);
  };

  const closeModal = () => {
    setModal(false); setSelSlot(null); setResult(null); setError("");
    setStuSearch(""); setStuOpen(false); setSelStudent("");
    setParentCard(null);
  };

  // When a student is selected in admin modal, fetch the parent's card on file
  const selectStudent = async (studentId, displayName) => {
    setSelStudent(String(studentId)); setStuSearch(displayName); setStuOpen(false);
    setParentCard(null); setCardLoading(true);
    try {
      const r = await fetch(`${API_BASE}/oto/payment-method-by-student/${studentId}`);
      const d = await r.json();
      setParentCard(d.card || null);
    } catch { setParentCard(null); }
    finally { setCardLoading(false); }
  };

  const submitBooking = async () => {
    if (!selStudent) { setError("Please select a student"); return; }
    setBooking(true); setError("");

    try {
      const payload = {
        student_id:       Number(selStudent),
        coach_id:         selCoach.user_id,
        session_date:     selDate,
        start_time:       selSlot.start_time,
        end_time:         selSlot.end_time,
        duration_minutes: selSlot.duration_minutes,
        booked_by_type:   isAdmin ? "admin" : "parent",
        booked_by_id:     currentUser.user_id,
        notes,
      };

      const res = await fetch(`${API_BASE}/oto/bookings`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (!d.success) { setError(d.message || "Booking failed"); setBooking(false); return; }

      // Backend auto-charges parent's saved card (off-session); no card input needed here
      setResult({ bookingId: d.booking_id, amount: d.amount_due, paid: !!d.auto_charged });
      refreshMonthSlots(selCoach.user_id, calYear, calMonth);
    } catch (e) { setError("Network error"); }
    setBooking(false);
  };

  const saveRate = async (coachId) => {
    setRateSaving(true); setRateMsg(null);
    const res = await fetch(`${API_BASE}/oto/coaches/${coachId}/rate`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ hourly_rate: Number(rateVal), pay_rate: Number(payRateVal) }),
    });
    const d = await res.json();
    setRateMsg({ ok: d.success, text: d.success ? "Rates saved" : d.message });
    if (d.success) {
      setCoaches(prev => prev.map(c => c.user_id === coachId
        ? { ...c, hourly_rate: Number(rateVal), pay_rate: Number(payRateVal) } : c));
      setEditRateId(null);
    }
    setRateSaving(false);
  };

  const weeks = buildCalendar(calYear, calMonth);
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}`;

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", color: textPrimary, padding: "28px 32px", maxWidth: 1300 }}>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <PageHeaderIcon icon={<FaCalendarCheck size={20} color={getBrandColors().blueDark} />} />
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: T ? "#fff" : C.blue }}>Book a 1-on-1 Session</div>
          <div style={{ fontSize: 13, color: textSecondary, marginTop: 3 }}>
            Select a coach, pick a date and available slot, then complete payment.
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "260px 300px 1fr", gap: 18, alignItems: "start" }}>

        {/* Coach List */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: textSecondary, marginBottom: 4 }}>Coaches</div>
          {coaches.length === 0 && <div style={{ color: textSecondary, fontSize: 13 }}>No coaches found</div>}
          {coaches.map(c => (
            <div key={c.user_id}
              onClick={() => { setSelCoach(c); setEditRateId(null); setRateMsg(null); }}
              style={{
                ...card(T), cursor: "pointer", transition: "all 0.14s",
                borderColor: selCoach?.user_id === c.user_id ? C.yellow : undefined,
                background: selCoach?.user_id === c.user_id
                  ? (T ? "rgba(247,206,11,0.12)" : "rgba(247,206,11,0.14)") : undefined,
              }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{c.full_name}</div>
              <div style={{ fontSize: 12, color: textSecondary, marginTop: 3 }}>{c.email}</div>
              {/* Rate display / edit */}
              <div style={{ marginTop: 8 }}>
                {editRateId === c.user_id ? (
                  <div onClick={e => e.stopPropagation()}>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "flex-end" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, color: textSecondary, marginBottom: 2 }}>Billing Rate (parent pays)</div>
                        <input type="number" value={rateVal} onChange={e => setRateVal(e.target.value)}
                          style={{ ...inputSt(T), width: "100%", padding: "4px 8px", boxSizing: "border-box" }} placeholder="0.00" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, color: textSecondary, marginBottom: 2 }}>Pay Rate (coach earns)</div>
                        <input type="number" value={payRateVal} onChange={e => setPayRateVal(e.target.value)}
                          style={{ ...inputSt(T), width: "100%", padding: "4px 8px", boxSizing: "border-box" }} placeholder="0.00" />
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => saveRate(c.user_id)} disabled={rateSaving} style={{ ...btn(true, true), flex: 1 }}>Save</button>
                      <button onClick={() => setEditRateId(null)} style={{ ...btn(false, true), background: "transparent", border: "1px solid rgba(3,78,123,0.25)", color: textSecondary }}>✕</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div>
                      <span style={{ fontSize: 13, fontWeight: 800, color: C.blue }}>
                        ${Number(c.hourly_rate || 0).toFixed(2)}/hr
                      </span>
                      {c.pay_rate > 0 && (
                        <span style={{ fontSize: 10, color: textSecondary, marginLeft: 6 }}>
                          (pays coach ${Number(c.pay_rate).toFixed(2)})
                        </span>
                      )}
                    </div>
                    {isAdmin && (
                      <button onClick={e => { e.stopPropagation(); setEditRateId(c.user_id); setRateVal(c.hourly_rate || ""); setPayRateVal(c.pay_rate || ""); setRateMsg(null); }}
                        style={{ ...btn(false, true), background: "transparent", border: "1px solid rgba(3,78,123,0.25)", color: textSecondary, fontSize: 11 }}>
                        Edit Rates
                      </button>
                    )}
                  </div>
                )}
              </div>
              {rateMsg && editRateId === null && (
                <div style={{ fontSize: 11, color: rateMsg.ok ? getBrandColors().green : getBrandColors().red, marginTop: 4 }}>{rateMsg.text}</div>
              )}
            </div>
          ))}
        </div>

        {/* Calendar */}
        <div style={card(T)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <button onClick={() => {
              let m = calMonth - 1, y = calYear;
              if (m < 1) { m = 12; y--; }
              setCalMonth(m); setCalYear(y);
            }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: textSecondary }}>‹</button>
            <span style={{ fontWeight: 700, fontSize: 14 }}>
              {MONTHS_LONG[calMonth-1]} {calYear}
              {slotsLoading && <span style={{ fontSize: 11, color: textSecondary, marginLeft: 8 }}>loading…</span>}
            </span>
            <button onClick={() => {
              let m = calMonth + 1, y = calYear;
              if (m > 12) { m = 1; y++; }
              setCalMonth(m); setCalYear(y);
            }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: textSecondary }}>›</button>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {DAYS_SHORT.map(d => (
                  <th key={d} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: textSecondary, padding: "4px 0", textAlign: "center" }}>{d}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week, wi) => (
                <tr key={wi}>
                  {week.map((day, di) => {
                    if (!day) return <td key={di} />;
                    const dateStr  = `${calYear}-${String(calMonth).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
                    const isPast   = dateStr < todayStr;
                    const isSel    = dateStr === selDate;
                    const isToday  = dateStr === todayStr;
                    const hasSlots = !isPast && !!monthSlots[dateStr]?.length;
                    const clickable = selCoach && !isPast && !slotsLoading;
                    return (
                      <td key={di} onClick={() => clickable && pickDate(day)}
                        style={{
                          textAlign: "center", padding: "5px 2px",
                          cursor: clickable ? "pointer" : "default",
                        }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: "50%", margin: "0 auto",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 13, fontWeight: isSel || isToday ? 700 : 400,
                          background: isSel ? C.yellow : isToday ? (T ? "rgba(255,255,255,0.12)" : "rgba(3,78,123,0.12)") : "transparent",
                          color: isSel ? "#1a1200" : isPast ? (T ? "rgba(255,255,255,0.22)" : "rgba(3,78,123,0.25)") : textPrimary,
                          boxShadow: hasSlots && !isSel ? `0 0 0 2px ${C.yellow}` : "none",
                        }}>{day}</div>
                        {hasSlots && !isSel && (
                          <div style={{ width: 4, height: 4, borderRadius: "50%", background: C.yellow, margin: "2px auto 0" }} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {!selCoach && (
            <div style={{ marginTop: 12, fontSize: 12, color: textSecondary, textAlign: "center" }}>
              ← Select a coach first
            </div>
          )}
        </div>

        {/* Slots — instant from prefetched cache */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: textSecondary, marginBottom: 10 }}>
            {selDate ? `Available Slots — ${fmtDate(selDate)}` : selCoach ? "Pick a highlighted date" : "Select a coach first"}
          </div>
          {selDate && (() => {
            const daySlots = monthSlots[selDate] || [];
            if (daySlots.length === 0) return (
              <div style={{ ...card(T), color: textSecondary, textAlign: "center", padding: "32px 20px" }}>
                No available slots for this date.<br />
                <span style={{ fontSize: 12 }}>All slots may be booked or the coach has no availability on this day.</span>
              </div>
            );
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {daySlots.map((s, si) => (
                  <div key={si} style={{ ...card(T), display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtTime(s.start_time)} – {fmtTime(s.end_time)}</div>
                      <div style={{ fontSize: 12, color: textSecondary, marginTop: 3 }}>
                        {s.duration_minutes} min &nbsp;·&nbsp; ${Number(s.amount_due).toFixed(2)}
                      </div>
                    </div>
                    <button onClick={() => openModal(s)} style={btn(true)}>Book</button>
                  </div>
                ))}
              </div>
            );
          })()}
          {!selDate && selCoach && !slotsLoading && Object.keys(monthSlots).length === 0 && (
            <div style={{ ...card(T), color: textSecondary, textAlign: "center", padding: "32px 20px", fontSize: 13 }}>
              No availability set for this month.
            </div>
          )}
        </div>
      </div>

      {/* Booking Modal */}
      {modal && selSlot && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.60)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ ...card(T), width: 460, padding: 28, maxHeight: "90vh", overflowY: "auto" }}>

            {result ? (
              /* Success screen */
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>{result.paid ? "✅" : "📋"}</div>
                <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>
                  {result.paid ? "Booking Confirmed & Charged!" : "Booking Created"}
                </div>
                <div style={{ fontSize: 13, color: textSecondary, marginBottom: 4 }}>Booking #{result.bookingId}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: C.blue, marginBottom: 12 }}>
                  {currency} ${Number(result.amount).toFixed(2)}
                </div>
                {!result.paid && (
                  <div style={{ padding: "10px 14px", background: "rgba(247,206,11,0.12)", borderRadius: 8, fontSize: 12, color: "#92610a", marginBottom: 14 }}>
                    ⚠️ No card on file for this parent. Booking is <strong>Pending Payment</strong> — confirm manually in the 1-on-1 Dashboard once payment is received.
                  </div>
                )}
                <button onClick={closeModal} style={btn(true)}>Close</button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Confirm Booking</div>
                <div style={{ fontSize: 12, color: textSecondary, marginBottom: 18 }}>
                  {selCoach?.full_name} &nbsp;·&nbsp; {fmtDate(selDate)} &nbsp;·&nbsp; {fmtTime(selSlot.start_time)}–{fmtTime(selSlot.end_time)}
                  &nbsp;·&nbsp; <strong>${Number(selSlot.amount_due).toFixed(2)}</strong>
                </div>

                {/* Student fuzzy search */}
                <div style={{ marginBottom: 14, position: "relative" }}>
                  <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: textSecondary, display: "block", marginBottom: 6 }}>Student *</label>
                  <input
                    type="text"
                    placeholder="Type student name to search…"
                    value={stuSearch}
                    onChange={e => { setStuSearch(e.target.value); setStuOpen(true); setSelStudent(""); searchStudents(e.target.value); }}
                    onFocus={() => setStuOpen(true)}
                    style={{ ...inputSt(T), paddingRight: selStudent ? 30 : 11 }}
                    autoComplete="off"
                  />
                  {selStudent && (
                    <span
                      onClick={() => { setSelStudent(""); setStuSearch(""); setStuOpen(false); setParentCard(null); }}
                      style={{ position: "absolute", right: 10, top: 32, cursor: "pointer", opacity: 0.5, fontSize: 15 }}
                    >✕</span>
                  )}
                  {stuOpen && stuSearch.length > 0 && (() => {
                    const filtered = students.slice(0, 10);
                    return filtered.length > 0 ? (
                      <div style={{
                        position: "absolute", zIndex: 99, width: "100%", top: "100%", left: 0,
                        background: T ? "#1e2a3a" : "#fff",
                        border: T ? "1px solid rgba(255,255,255,0.14)" : "1px solid rgba(3,78,123,0.18)",
                        borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.18)", maxHeight: 220, overflowY: "auto",
                      }}>
                        {filtered.map(s => (
                          <div
                            key={s.student_id}
                            onMouseDown={() => selectStudent(s.student_id, `${s.first_name} ${s.last_name}`)}
                            style={{
                              padding: "9px 14px", cursor: "pointer", fontSize: 13,
                              color: T ? "rgba(255,255,255,0.88)" : "#1a2a3a",
                              borderBottom: T ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(3,78,123,0.07)",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = T ? "rgba(255,255,255,0.08)" : "rgba(3,78,123,0.06)"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                          >
                            {s.first_name} {s.last_name}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{
                        position: "absolute", zIndex: 99, width: "100%", top: "100%", left: 0,
                        background: T ? "#1e2a3a" : "#fff",
                        border: T ? "1px solid rgba(255,255,255,0.14)" : "1px solid rgba(3,78,123,0.18)",
                        borderRadius: 8, padding: "10px 14px", fontSize: 13, opacity: 0.55,
                      }}>No students found</div>
                    );
                  })()}
                </div>

                <div style={{ marginBottom: 18 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: textSecondary, display: "block", marginBottom: 6 }}>Notes (optional)</label>
                  <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Session goals, focus areas…"
                    style={{ ...inputSt(T), resize: "vertical" }} />
                </div>

                {/* Parent's card on file (fetched when student selected) */}
                {selStudent && (
                  <div style={{ marginBottom: 18 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: textSecondary, display: "block", marginBottom: 8 }}>
                      Parent Payment on File
                    </label>
                    {cardLoading ? (
                      <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 12, opacity: 0.55,
                        background: T ? "rgba(255,255,255,0.05)" : "#f4f7fb" }}>
                        Checking parent's card…
                      </div>
                    ) : parentCard ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 12,
                        background: T ? "rgba(16,185,129,0.10)" : "rgba(16,185,129,0.07)",
                        border: "1.5px solid rgba(16,185,129,0.30)", borderRadius: 10, padding: "12px 14px" }}>
                        <div style={{ flexShrink: 0, width: 46, height: 30, borderRadius: 6,
                          background: T ? "rgba(255,255,255,0.10)" : "rgba(3,78,123,0.08)",
                          border: T ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(3,78,123,0.15)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 9, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em",
                          color: T ? "#94a3b8" : "#475569" }}>
                          {parentCard.brand || "CARD"}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.06em",
                            color: T ? "#e2e8f0" : "#1a2840" }}>
                            •••• •••• •••• {parentCard.last4}
                          </div>
                          <div style={{ fontSize: 11, marginTop: 2, opacity: 0.60, display: "flex", gap: 10 }}>
                            <span>Exp {String(parentCard.exp_month).padStart(2,"0")}/{parentCard.exp_year}</span>
                          </div>
                        </div>
                        <div style={{ flexShrink: 0, fontSize: 11, fontWeight: 700, color: getBrandColors().green,
                          background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.28)",
                          borderRadius: 6, padding: "3px 10px" }}>
                          Will be charged
                        </div>
                      </div>
                    ) : (
                      <div style={{ padding: "10px 14px", borderRadius: 8, fontSize: 12,
                        background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.30)",
                        color: T ? getBrandColors().orange : "#92400e" }}>
                        ⚠️ No card on file for this student's parent. Booking will be created as <strong>Pending Payment</strong>.
                      </div>
                    )}
                  </div>
                )}

                {error && (
                  <div style={{ marginBottom: 14, padding: "10px 14px", background: "rgba(229,62,62,0.10)", borderRadius: 8, fontSize: 13, color: getBrandColors().red }}>{error}</div>
                )}

                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={submitBooking} disabled={booking} style={{ ...btn(true, false, booking), flex: 1 }}>
                    {booking ? "Processing…" : parentCard ? `Confirm & Charge $${Number(selSlot?.amount_due).toFixed(2)}` : "Create Booking"}
                  </button>
                  <button onClick={closeModal} style={{ ...btn(false, false, false), flex: 1, background: "transparent",
                    border: `1px solid ${T ? "rgba(255,255,255,0.20)" : "rgba(3,78,123,0.22)"}`, color: textSecondary }}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
